import { useCallback, useEffect } from 'react';
import { LayoutChangeEvent } from 'react-native';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolate,
  Easing,
} from 'react-native-reanimated';

const ACCORDION_DURATION = 300;
const ACCORDION_EASING = Easing.bezier(0.4, 0, 0.2, 1);

interface UseAccordionAnimationOptions {
  initiallyOpen?: boolean;
  duration?: number;
  onToggle?: (isOpen: boolean) => void;
}

export function useAccordionAnimation(options: UseAccordionAnimationOptions = {}) {
  const {
    initiallyOpen = false,
    duration = ACCORDION_DURATION,
    onToggle,
  } = options;

  const progress = useSharedValue(initiallyOpen ? 1 : 0);
  const measuredHeight = useSharedValue(0);
  const isOpen = useSharedValue(initiallyOpen);

  const animateToValue = useCallback((toValue: number) => {
    'worklet';
    progress.value = withTiming(toValue, {
      duration,
      easing: ACCORDION_EASING,
    });
  }, [progress, duration]);

  const toggle = useCallback(() => {
    const newValue = !isOpen.value;
    isOpen.value = newValue;
    animateToValue(newValue ? 1 : 0);
    onToggle?.(newValue);
  }, [isOpen, animateToValue, onToggle]);

  const open = useCallback(() => {
    if (isOpen.value) return;
    isOpen.value = true;
    animateToValue(1);
    onToggle?.(true);
  }, [isOpen, animateToValue, onToggle]);

  const close = useCallback(() => {
    if (!isOpen.value) return;
    isOpen.value = false;
    animateToValue(0);
    onToggle?.(false);
  }, [isOpen, animateToValue, onToggle]);

  const setOpen = useCallback((value: boolean) => {
    if (isOpen.value === value) return;
    isOpen.value = value;
    animateToValue(value ? 1 : 0);
  }, [isOpen, animateToValue]);

  const setOpenImmediate = useCallback((value: boolean) => {
    isOpen.value = value;
    progress.value = value ? 1 : 0;
  }, [isOpen, progress]);

  const onContentLayout = useCallback((e: LayoutChangeEvent) => {
    const height = e.nativeEvent.layout.height;
    if (height > 0) {
      measuredHeight.value = height;
    }
  }, [measuredHeight]);

  const animatedHeightStyle = useAnimatedStyle(() => {
    const height = interpolate(
      progress.value,
      [0, 1],
      [0, measuredHeight.value],
    );
    const opacity = interpolate(
      progress.value,
      [0, 0.3, 1],
      [0, 0, 1],
    );
    return {
      height,
      opacity,
      overflow: 'hidden' as const,
    };
  });

  const animatedChevronStyle = useAnimatedStyle(() => {
    const rotate = interpolate(
      progress.value,
      [0, 1],
      [0, 180],
    );
    return {
      transform: [{ rotate: `${rotate}deg` }],
    };
  });

  return {
    progress,
    isOpen,
    toggle,
    open,
    close,
    setOpen,
    setOpenImmediate,
    onContentLayout,
    animatedHeightStyle,
    animatedChevronStyle,
  };
}
